/**
 * "Forgot password" (1.0.0-rc.4), only when email is set up: ask for a link, then choose a new password from it.
 * The link's token is in the address's fragment (#token=…), so it never reaches a server log or a Referer.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail } from 'lucide-react';
import { api, errorMessage } from '../api/client';
import { LoginFrame } from './Login';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/v1/auth/forgot-password', { method: 'POST', json: { email } });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <LoginFrame>
      <form className="login-form" style={{ width: '100%' }} onSubmit={submit} data-testid="forgot-form">
        <h1>Forgot your password?</h1>
        {sent ? (
          <div className="banner info" role="status" data-testid="forgot-sent"><div className="banner-body"><strong>Check your email</strong>
            <span>If {email} has a PRISM account that signs in with a password, a link to choose a new one is on its way. It works once, for a short while.</span></div></div>
        ) : (
          <>
            <p className="muted" style={{ margin: 0 }}>Enter your email address. PRISM sends you a link to choose a new password.</p>
            {error && <div className="banner error" role="alert">{error}</div>}
            <div className="field"><label htmlFor="forgot-email">Email</label>
              <input id="forgot-email" className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus /></div>
            <button className="btn primary" type="submit" disabled={busy} style={{ height: 38 }}>{busy ? <Loader2 size={16} className="spin" /> : <><Mail size={15} /> Send the link</>}</button>
          </>
        )}
        <Link to="/login" className="faint" style={{ fontSize: 12.5 }}><ArrowLeft size={12} style={{ verticalAlign: -1 }} /> Back to sign-in</Link>
      </form>
    </LoginFrame>
  );
}

export function ResetPassword() {
  const [token] = useState(() => /(?:^#|&)token=([^&]+)/.exec(window.location.hash)?.[1] ?? '');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(token ? null : 'This link is not complete. Open it again from the email, or ask for a new one.');
  const [busy, setBusy] = useState(false);
  // The token leaves the address bar (and the history) as soon as it is read.
  useEffect(() => { if (window.location.hash) window.history.replaceState(null, '', window.location.pathname); }, []);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (next !== again) return setError('The passwords are not the same');
    setBusy(true);
    setError(null);
    try {
      await api('/api/v1/auth/reset-password', { method: 'POST', json: { token, newPassword: next } });
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <LoginFrame>
      <form className="login-form" style={{ width: '100%' }} onSubmit={submit} data-testid="reset-form">
        <h1>Choose a new password</h1>
        {done ? (
          <div className="banner info" role="status" data-testid="reset-done"><div className="banner-body"><strong>Your password is changed</strong>
            <span>You were signed out everywhere. Sign in with the new password.</span></div></div>
        ) : (
          <>
            {error && <div className="banner error" role="alert">{error}</div>}
            <div className="field"><label htmlFor="reset-new">New password</label>
              <input id="reset-new" className="input" type="password" autoComplete="new-password" minLength={10} value={next} onChange={(e) => setNext(e.target.value)} required autoFocus />
              <span className="help">At least 10 characters. A few words together are easy to remember and hard to guess.</span></div>
            <div className="field"><label htmlFor="reset-again">New password again</label>
              <input id="reset-again" className="input" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} required /></div>
            <button className="btn primary" type="submit" disabled={busy || !token || next.length < 10} style={{ height: 38 }}>{busy ? <Loader2 size={16} className="spin" /> : 'Set the password'}</button>
          </>
        )}
        <Link to="/login" className="faint" style={{ fontSize: 12.5 }}>Sign in</Link>
      </form>
    </LoginFrame>
  );
}
